import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from '../firebase';
import { defaultProblemLabels, defaultProblemStatuses, normalizeProblemChips } from '../data/problems';
import { listReadablePrivacyGroupIds } from './categoryService';
import { createUserNotification } from './notificationService';

function requireDb() {
  if (!db) {
    throw new Error('Firestore is not configured. Add REACT_APP_FIREBASE_* values to .env.local.');
  }
}

function timestampValue(value) {
  if (!value) {
    return 0;
  }
  if (typeof value.toMillis === 'function') {
    return value.toMillis();
  }
  return Number(new Date(value)) || 0;
}

function sortNewestFirst(items) {
  return [...items].sort((a, b) => timestampValue(b.updatedAt || b.createdAt) - timestampValue(a.updatedAt || a.createdAt));
}

function sortOldestFirst(items) {
  return [...items].sort((a, b) => timestampValue(a.createdAt || a.updatedAt) - timestampValue(b.createdAt || b.updatedAt));
}

function limitText(value, max) {
  return String(value || '').trim().slice(0, max);
}

function sortTimeline(items) {
  return [...items].sort((a, b) => (b.sortYear || 0) - (a.sortYear || 0));
}

async function listByOwner(collectionName, ownerId, publicOnly = false) {
  requireDb();
  const constraints = [where('ownerId', '==', ownerId)];
  if (publicOnly) {
    constraints.push(where('visibility', '==', 'public'));
  }
  const snapshot = await getDocs(query(collection(db, collectionName), ...constraints));
  return snapshot.docs.map((entry) => ({ id: entry.id, ...entry.data() }));
}

async function isFriendWithOwner(ownerId, viewerUid) {
  if (!viewerUid || viewerUid === ownerId) {
    return false;
  }

  requireDb();
  const snapshot = await getDoc(doc(db, 'profileFriends', ownerId, 'friends', viewerUid));
  return snapshot.exists();
}

async function listVisibleByOwner(collectionName, ownerId, viewerUid = null) {
  requireDb();
  if (viewerUid === ownerId) {
    return listByOwner(collectionName, ownerId);
  }

  const baseRef = collection(db, collectionName);
  const queries = [getDocs(query(baseRef, where('ownerId', '==', ownerId), where('visibility', '==', 'public')))];

  if (await isFriendWithOwner(ownerId, viewerUid)) {
    queries.push(getDocs(query(baseRef, where('ownerId', '==', ownerId), where('visibility', '==', 'friends'))));
  }

  const readableGroupIds = await listReadablePrivacyGroupIds(ownerId, viewerUid);
  if (readableGroupIds.length) {
    queries.push(getDocs(query(
      baseRef,
      where('ownerId', '==', ownerId),
      where('visibility', '==', 'group'),
      where('visibilityGroupId', 'in', readableGroupIds)
    )));
  }

  const snapshots = await Promise.all(queries);
  const merged = new Map();
  snapshots.forEach((snapshot) => {
    snapshot.docs.forEach((entry) => merged.set(entry.id, { id: entry.id, ...entry.data() }));
  });
  return Array.from(merged.values());
}

function visibilityGroupId(input) {
  return input.visibility === 'group' ? String(input.visibilityGroupId || '').trim() : '';
}

export async function listOwnerStories(ownerId) {
  return sortNewestFirst(await listByOwner('stories', ownerId));
}

export async function listPublicStories(ownerId) {
  return sortNewestFirst(await listByOwner('stories', ownerId, true));
}

export async function listVisibleStories(ownerId, viewerUid = null) {
  return sortNewestFirst(await listVisibleByOwner('stories', ownerId, viewerUid));
}

export async function getPublicStoryById(storyId, ownerId, viewerUid = null) {
  requireDb();
  let snapshot;
  try {
    snapshot = await getDoc(doc(db, 'stories', storyId));
  } catch (err) {
    if (err.code === 'permission-denied') {
      return null;
    }
    throw err;
  }

  if (!snapshot.exists()) {
    return null;
  }

  const story = { id: snapshot.id, ...snapshot.data() };
  if (story.ownerId !== ownerId) {
    return null;
  }
  if (story.visibility === 'private' && viewerUid !== ownerId) {
    return null;
  }
  if (story.visibility === 'friends' && viewerUid !== ownerId && !(await isFriendWithOwner(ownerId, viewerUid))) {
    return null;
  }
  if (story.visibility === 'group' && viewerUid !== ownerId) {
    const readableGroupIds = await listReadablePrivacyGroupIds(ownerId, viewerUid);
    if (!readableGroupIds.includes(story.visibilityGroupId)) {
      return null;
    }
  }

  return story;
}

export async function createStory(ownerId, story) {
  requireDb();
  const now = serverTimestamp();
  const ref = await addDoc(collection(db, 'stories'), {
    ownerId,
    title: story.title.trim(),
    summary: story.summary.trim(),
    content: story.content.trim(),
    visibility: story.visibility,
    visibilityGroupId: visibilityGroupId(story),
    createdAt: now,
    updatedAt: now,
  });
  return ref.id;
}

export async function updateStory(storyId, story) {
  requireDb();
  await updateDoc(doc(db, 'stories', storyId), {
    title: story.title.trim(),
    summary: story.summary.trim(),
    content: story.content.trim(),
    visibility: story.visibility,
    visibilityGroupId: visibilityGroupId(story),
    updatedAt: serverTimestamp(),
  });
}

export async function deleteStory(storyId) {
  requireDb();
  await deleteDoc(doc(db, 'stories', storyId));
}

export async function listOwnerTimelineEvents(ownerId) {
  return sortTimeline(await listByOwner('timelineEvents', ownerId));
}

export async function listPublicTimelineEvents(ownerId) {
  return sortTimeline(await listByOwner('timelineEvents', ownerId, true));
}

export async function listVisibleTimelineEvents(ownerId, viewerUid = null) {
  return sortTimeline(await listVisibleByOwner('timelineEvents', ownerId, viewerUid));
}

export async function createTimelineEvent(ownerId, event) {
  requireDb();
  const now = serverTimestamp();
  const ref = await addDoc(collection(db, 'timelineEvents'), {
    ownerId,
    title: event.title.trim(),
    dateLabel: event.dateLabel.trim(),
    sortYear: Number(event.sortYear) || 0,
    description: event.description.trim(),
    visibility: event.visibility,
    visibilityGroupId: visibilityGroupId(event),
    createdAt: now,
    updatedAt: now,
  });
  return ref.id;
}

export async function updateTimelineEvent(eventId, event) {
  requireDb();
  await updateDoc(doc(db, 'timelineEvents', eventId), {
    title: event.title.trim(),
    dateLabel: event.dateLabel.trim(),
    sortYear: Number(event.sortYear) || 0,
    description: event.description.trim(),
    visibility: event.visibility,
    visibilityGroupId: visibilityGroupId(event),
    updatedAt: serverTimestamp(),
  });
}

export async function deleteTimelineEvent(eventId) {
  requireDb();
  await deleteDoc(doc(db, 'timelineEvents', eventId));
}

export async function listOwnerPageEntries(ownerId) {
  return sortNewestFirst(await listByOwner('pageEntries', ownerId));
}

export async function listVisiblePageEntries(ownerId, viewerUid = null) {
  return sortNewestFirst(await listVisibleByOwner('pageEntries', ownerId, viewerUid));
}

export async function createPageEntry(ownerId, entry) {
  requireDb();
  const now = serverTimestamp();
  const ref = await addDoc(collection(db, 'pageEntries'), {
    ownerId,
    prompt: entry.prompt.trim(),
    theme: entry.theme.trim(),
    answer: entry.answer.trim(),
    pageName: String(entry.pageName || 'Default').trim(),
    imageUrl: String(entry.imageUrl || '').trim(),
    visibility: entry.visibility,
    visibilityGroupId: visibilityGroupId(entry),
    showOnProfile: Boolean(entry.showOnProfile),
    tileFormat: entry.tileFormat || 'square',
    entryDate: entry.entryDate || new Date().toISOString().slice(0, 10),
    createdAt: now,
    updatedAt: now,
  });
  return ref.id;
}

export async function updatePageEntry(entryId, entry) {
  requireDb();
  await updateDoc(doc(db, 'pageEntries', entryId), {
    prompt: entry.prompt.trim(),
    theme: entry.theme.trim(),
    answer: entry.answer.trim(),
    pageName: String(entry.pageName || 'Default').trim(),
    imageUrl: String(entry.imageUrl || '').trim(),
    visibility: entry.visibility,
    visibilityGroupId: visibilityGroupId(entry),
    showOnProfile: Boolean(entry.showOnProfile),
    tileFormat: entry.tileFormat || 'square',
    entryDate: entry.entryDate || new Date().toISOString().slice(0, 10),
    updatedAt: serverTimestamp(),
  });
}

export async function deletePageEntry(entryId) {
  requireDb();
  await deleteDoc(doc(db, 'pageEntries', entryId));
}

export async function listPageComments(entryId) {
  requireDb();
  const snapshot = await getDocs(collection(db, 'pageComments', entryId, 'comments'));
  return sortOldestFirst(snapshot.docs.map((entry) => ({ id: entry.id, ...entry.data() })));
}

export async function createPageComment(entry, authorProfile, text) {
  requireDb();
  const now = serverTimestamp();
  const ref = await addDoc(collection(db, 'pageComments', entry.id, 'comments'), {
    entryId: entry.id,
    entryOwnerId: entry.ownerId,
    authorId: authorProfile.ownerId,
    authorName: authorProfile.displayName || authorProfile.username || 'Reader',
    text: text.trim(),
    createdAt: now,
    updatedAt: now,
  });
  await createUserNotification(entry.ownerId, {
    actorId: authorProfile.ownerId,
    actorName: authorProfile.displayName || authorProfile.username || 'Reader',
    type: 'pageComment',
    title: 'New page comment',
    body: `${authorProfile.displayName || authorProfile.username || 'Someone'} commented on your page tile.`,
    href: '/dashboard/pages',
  });
  return ref.id;
}

export async function deletePageComment(entryId, commentId) {
  requireDb();
  await deleteDoc(doc(db, 'pageComments', entryId, 'comments', commentId));
}

export async function listOwnerProblemDocs(ownerId) {
  return sortNewestFirst(await listByOwner('problemDocs', ownerId));
}

export async function listDiscoverableProblemDocs(ownerId) {
  requireDb();
  const publicSnapshot = await getDocs(query(collection(db, 'problemDocs'), where('visibility', '==', 'public')));
  const publicDocs = publicSnapshot.docs.map((entry) => ({ id: entry.id, ...entry.data() }));
  const ownerDocs = ownerId ? await listOwnerProblemDocs(ownerId) : [];
  const merged = new Map([...publicDocs, ...ownerDocs].map((entry) => [entry.id, entry]));
  return sortNewestFirst(Array.from(merged.values()));
}

function problemLabels(docInput) {
  return normalizeProblemChips(docInput.labels || docInput.topic, [docInput.topic || defaultProblemLabels[defaultProblemLabels.length - 1]]);
}

function problemStatuses(docInput) {
  return normalizeProblemChips(docInput.statuses || docInput.status, [docInput.status || defaultProblemStatuses[0]]);
}

function problemSource(docInput) {
  return {
    sourceType: limitText(docInput.sourceType, 80),
    sourceId: limitText(docInput.sourceId, 160),
    sourceTitle: limitText(docInput.sourceTitle, 500),
    sourceText: limitText(docInput.sourceText, 5000),
  };
}

function problemPayload(ownerId, docInput, authorProfile, now = null) {
  const labels = problemLabels(docInput);
  const statuses = problemStatuses(docInput);
  const source = problemSource(docInput);
  const searchableText = [
    limitText(docInput.title, 180),
    limitText(docInput.summary, 600),
    limitText(docInput.problem, 10000),
    limitText(docInput.solution, 10000),
    limitText(docInput.keyPoints, 3000),
    labels.join(' '),
    statuses.join(' '),
    source.sourceTitle,
    source.sourceText,
  ].join(' ').toLowerCase().slice(0, 25000);
  const payload = {
    ownerId,
    title: limitText(docInput.title, 180),
    summary: limitText(docInput.summary, 600),
    problem: limitText(docInput.problem, 10000),
    solution: limitText(docInput.solution, 10000),
    keyPoints: limitText(docInput.keyPoints, 3000),
    labels,
    statuses,
    topic: labels[0] || defaultProblemLabels[defaultProblemLabels.length - 1],
    status: statuses[0] || defaultProblemStatuses[0],
    visibility: docInput.visibility,
    visibilityGroupId: visibilityGroupId(docInput),
    anonymous: Boolean(docInput.anonymous),
    displayName: docInput.anonymous ? 'Anonymous user' : (authorProfile.displayName || authorProfile.username || 'Community member'),
    searchableText,
    ...source,
  };
  if (now) {
    payload.createdAt = now;
    payload.updatedAt = now;
  } else {
    payload.updatedAt = serverTimestamp();
  }
  return payload;
}

export async function createProblemDoc(ownerId, docInput, authorProfile) {
  requireDb();
  const now = serverTimestamp();
  const ref = await addDoc(collection(db, 'problemDocs'), problemPayload(ownerId, docInput, authorProfile, now));
  return ref.id;
}

export async function updateProblemDoc(docId, docInput, authorProfile) {
  requireDb();
  await updateDoc(doc(db, 'problemDocs', docId), problemPayload(docInput.ownerId || authorProfile.ownerId, docInput, authorProfile));
}

export async function deleteProblemDoc(docId) {
  requireDb();
  await deleteDoc(doc(db, 'problemDocs', docId));
}

export async function createReport(reporterId, targetType, targetId, reason) {
  requireDb();
  const ref = await addDoc(collection(db, 'reports'), {
    reporterId,
    targetType,
    targetId,
    reason: reason.trim(),
    status: 'open',
    createdAt: serverTimestamp(),
  });
  return ref.id;
}

export async function listBlockedUsers(ownerId) {
  requireDb();
  const snapshot = await getDocs(collection(db, 'userBlocks', ownerId, 'blocked'));
  return snapshot.docs.map((entry) => entry.id);
}

export async function blockUser(ownerId, blockedUid) {
  requireDb();
  await setDoc(doc(db, 'userBlocks', ownerId, 'blocked', blockedUid), {
    blockerId: ownerId,
    blockedUid,
    createdAt: serverTimestamp(),
  });
}

export async function unblockUser(ownerId, blockedUid) {
  requireDb();
  await deleteDoc(doc(db, 'userBlocks', ownerId, 'blocked', blockedUid));
}

export async function createProblemContactRequest(senderProfile, problemDoc, message = '') {
  requireDb();
  if (!senderProfile?.ownerId) {
    throw new Error('Create your profile before asking to connect.');
  }
  if (!problemDoc?.ownerId || problemDoc.ownerId === senderProfile.ownerId) {
    throw new Error('Choose another contributor to contact.');
  }

  const now = serverTimestamp();
  const ref = await addDoc(collection(db, 'problemContactRequests'), {
    fromUid: senderProfile.ownerId,
    fromDisplayName: senderProfile.displayName || senderProfile.username || 'Community member',
    fromUsername: senderProfile.username || '',
    toUid: problemDoc.ownerId,
    toDisplayName: problemDoc.displayName || 'Problem contributor',
    problemId: problemDoc.id,
    problemTitle: problemDoc.title || 'Problem document',
    topic: problemDoc.topic || 'other',
    message: String(message || '').trim() || 'I found your problem document relevant and would like to connect around this topic.',
    status: 'pending',
    createdAt: now,
    updatedAt: now,
  });
  await createUserNotification(problemDoc.ownerId, {
    actorId: senderProfile.ownerId,
    actorName: senderProfile.displayName || senderProfile.username || 'Community member',
    type: 'problemContactRequest',
    title: 'New problem contact request',
    body: `${senderProfile.displayName || senderProfile.username || 'Someone'} asked to connect about ${problemDoc.title || 'a problem'}.`,
    href: '/dashboard/problems',
  });
  return ref.id;
}

export async function listIncomingProblemContactRequests(uid) {
  requireDb();
  const snapshot = await getDocs(query(
    collection(db, 'problemContactRequests'),
    where('toUid', '==', uid),
    where('status', '==', 'pending')
  ));
  return sortNewestFirst(snapshot.docs.map((entry) => ({ id: entry.id, ...entry.data() })));
}

export async function listOutgoingProblemContactRequests(uid) {
  requireDb();
  const snapshot = await getDocs(query(
    collection(db, 'problemContactRequests'),
    where('fromUid', '==', uid),
    where('status', '==', 'pending')
  ));
  return sortNewestFirst(snapshot.docs.map((entry) => ({ id: entry.id, ...entry.data() })));
}

export async function listAcceptedProblemContactRequests(uid) {
  requireDb();
  const [sentSnapshot, receivedSnapshot] = await Promise.all([
    getDocs(query(
      collection(db, 'problemContactRequests'),
      where('fromUid', '==', uid),
      where('status', '==', 'accepted')
    )),
    getDocs(query(
      collection(db, 'problemContactRequests'),
      where('toUid', '==', uid),
      where('status', '==', 'accepted')
    )),
  ]);
  const merged = [...sentSnapshot.docs, ...receivedSnapshot.docs].map((entry) => ({ id: entry.id, ...entry.data() }));
  return sortNewestFirst(merged);
}

export async function acceptProblemContactRequest(requestOrId) {
  requireDb();
  const requestId = typeof requestOrId === 'string' ? requestOrId : requestOrId.id;
  await updateDoc(doc(db, 'problemContactRequests', requestId), {
    status: 'accepted',
    updatedAt: serverTimestamp(),
  });
  if (typeof requestOrId !== 'string') {
    await createUserNotification(requestOrId.fromUid, {
      actorId: requestOrId.toUid,
      actorName: requestOrId.toDisplayName || 'Problem contributor',
      type: 'problemContactAccepted',
      title: 'Problem contact accepted',
      body: `${requestOrId.toDisplayName || 'A contributor'} accepted your contact request about ${requestOrId.problemTitle || 'a problem'}.`,
      href: '/dashboard/problems',
    });
  }
}

export async function declineProblemContactRequest(requestId) {
  requireDb();
  await updateDoc(doc(db, 'problemContactRequests', requestId), {
    status: 'declined',
    updatedAt: serverTimestamp(),
  });
}

export async function cancelProblemContactRequest(requestId) {
  requireDb();
  await updateDoc(doc(db, 'problemContactRequests', requestId), {
    status: 'cancelled',
    updatedAt: serverTimestamp(),
  });
}

export async function listProblemContactMessages(requestId) {
  requireDb();
  const snapshot = await getDocs(collection(db, 'problemContactMessages', requestId, 'messages'));
  return sortOldestFirst(snapshot.docs.map((entry) => ({ id: entry.id, ...entry.data() })));
}

export async function createProblemContactMessage(request, authorProfile, text) {
  requireDb();
  if (request.status !== 'accepted') {
    throw new Error('Messages are only available after the contact request is accepted.');
  }
  const ref = await addDoc(collection(db, 'problemContactMessages', request.id, 'messages'), {
    requestId: request.id,
    authorId: authorProfile.ownerId,
    authorName: authorProfile.displayName || authorProfile.username || 'Community member',
    text: text.trim(),
    createdAt: serverTimestamp(),
  });
  const recipientId = request.fromUid === authorProfile.ownerId ? request.toUid : request.fromUid;
  await createUserNotification(recipientId, {
    actorId: authorProfile.ownerId,
    actorName: authorProfile.displayName || authorProfile.username || 'Community member',
    type: 'problemMessage',
    title: 'New problem message',
    body: `${authorProfile.displayName || authorProfile.username || 'Someone'} sent a message about ${request.problemTitle || 'a problem'}.`,
    href: '/dashboard/problems',
  });
  return ref.id;
}

export async function deleteProblemContactMessage(requestId, messageId) {
  requireDb();
  await deleteDoc(doc(db, 'problemContactMessages', requestId, 'messages', messageId));
}

export async function listProblemComments(problemId) {
  requireDb();
  const snapshot = await getDocs(collection(db, 'problemComments', problemId, 'comments'));
  return sortOldestFirst(snapshot.docs.map((entry) => ({ id: entry.id, ...entry.data() })));
}

export async function createProblemComment(problemDoc, authorProfile, text, anonymous = false) {
  requireDb();
  const now = serverTimestamp();
  const ref = await addDoc(collection(db, 'problemComments', problemDoc.id, 'comments'), {
    problemId: problemDoc.id,
    problemOwnerId: problemDoc.ownerId,
    authorId: authorProfile.ownerId,
    authorName: anonymous ? 'Anonymous user' : (authorProfile.displayName || authorProfile.username || 'Community member'),
    anonymous: Boolean(anonymous),
    text: text.trim(),
    createdAt: now,
    updatedAt: now,
  });
  await createUserNotification(problemDoc.ownerId, {
    actorId: authorProfile.ownerId,
    actorName: anonymous ? 'Anonymous user' : (authorProfile.displayName || authorProfile.username || 'Community member'),
    type: 'problemComment',
    title: 'New problem comment',
    body: `${anonymous ? 'Anonymous user' : (authorProfile.displayName || authorProfile.username || 'Someone')} commented on ${problemDoc.title || 'your problem'}.`,
    href: '/dashboard/problems/search',
  });
  return ref.id;
}

export async function deleteProblemComment(problemId, commentId) {
  requireDb();
  await deleteDoc(doc(db, 'problemComments', problemId, 'comments', commentId));
}
